import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Accordion, AccordionDetails, AccordionSummary, ToggleButtonGroup } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ToggleButton } from '@linetweet/linetweet-ui';
import { useIntl } from 'react-intl';

import { EarMoldSectionProps, EarMoldToggleProps } from '../types';

import styles from './EarMoldSection.module.scss';

function EarMoldToggle({ value, onChange }: EarMoldToggleProps) {
  const intl = useIntl();

  const options = [
    { name: 'without', label: intl.formatMessage({ id: 'seriesbooking.earmold.without' }), value: false },
    { name: 'with', label: intl.formatMessage({ id: 'seriesbooking.earmold.with' }), value: true },
  ];

  return (
    <ToggleButtonGroup
      exclusive
      className={styles.toggleGroup}
      value={value}
      onChange={(_event, changedValue: boolean | null) => {
        if (changedValue === null) {
          return;
        }

        onChange(changedValue);
      }}
    >
      {options.map((option) => (
        <ToggleButton
          data-testid={`earmold-${option.name}-button`}
          key={option.name}
          color="primary"
          value={option.value}
        >
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

// Eligibility is computed once by the parent form (which also needs it to gate
// the metadata write on submit) and passed in, so this stays a pure component
// with no store access and no duplicate subscription.
function EarMoldSectionComponent({ isEligible }: EarMoldSectionProps) {
  const intl = useIntl();
  const form = useFormContext();

  if (!isEligible) {
    return <></>;
  }

  return (
    <Accordion defaultExpanded disableGutters className={styles.accordion} data-testid="earmold-section">
      <AccordionSummary expandIcon={<ExpandMoreIcon />} data-testid="earmold-section-summary">
        {intl.formatMessage({ id: 'seriesbooking.earmold.title' })}
      </AccordionSummary>
      <AccordionDetails>
        <Controller
          control={form.control}
          name="earmold"
          render={({ field }) => <EarMoldToggle value={field.value} onChange={field.onChange} />}
        />
      </AccordionDetails>
    </Accordion>
  );
}

export const EarMoldSection = React.memo(EarMoldSectionComponent);
